import { resolveExtrudeOperationFromKind } from './ExtrudeFeatureKindResolver';
import type { NormalizedExtrudeFeatureDefinition } from '../../../utils/feature/normalizeExtrudeFeatureDefinition';
import { normalizeExtrudeFeatureDefinition } from '../../../utils/feature/normalizeExtrudeFeatureDefinition';
import { ExtrudeCutDefinitionSnapshotBuilder } from '../../runtime/ExtrudeCutDefinitionSnapshotBuilder';
import { FeatureDefinition, type FeatureDefinitionSnapshot } from '../FeatureDefinition';

/**
 * 拉伸特征定义快照。
 */
export interface ExtrudeFeatureDefinitionSnapshot extends FeatureDefinitionSnapshot {
    /** 当前特征语义类型。 */
    featureKind: ExtrudeFeatureKind;
    /** 原始拉伸命令参数快照。 */
    commandParams: ExtrudeParams;
    /** 归一化后的拉伸定义。 */
    normalizedDefinition: NormalizedExtrudeFeatureDefinition;
}

/**
 * 拉伸特征定义投影快照。
 */
export interface ExtrudeFeatureDefinitionProjection {
    /** 当前特征语义类型。 */
    featureKind: ExtrudeFeatureKind;
    /** 当前特征操作类型。 */
    operation: ExtrudeOperation;
    /** 方向 1 定义。 */
    direction1: NormalizedExtrudeFeatureDefinition['direction1'];
    /** 方向 2 定义。 */
    direction2: NormalizedExtrudeFeatureDefinition['direction2'];
    /** 薄壁特征定义。 */
    thinFeature: NormalizedExtrudeFeatureDefinition['thinFeature'];
    /** 是否合并结果。 */
    mergeResult: boolean;
    /** 拉伸切除专属定义。 */
    cutOptions: ExtrudeCutDefinition | null;
}

/**
 * 拉伸特征定义对象。
 *
 * 设计说明：
 * 1. 定义对象统一包裹命令参数快照与归一化结果，避免 UI 和执行层各自解释同一份参数；
 * 2. 定义对象对外始终提供只读快照，避免编辑态与正式状态共享同一个参数引用；
 * 3. 后续新增同族特征时，只需扩展 definition，不再重复堆叠命令参数解析逻辑。
 */
export class ExtrudeFeatureDefinitionModel extends FeatureDefinition<ExtrudeFeatureDefinitionSnapshot> {
    /**
     * 从现有拉伸命令参数创建定义对象。
     *
     * @param input 输入参数。
     * @returns 拉伸特征定义对象。
     */
    public static fromParams(input: {
        featureKind: ExtrudeFeatureKind;
        commandParams: ExtrudeParams;
        version?: number;
    }): ExtrudeFeatureDefinitionModel {
        return new ExtrudeFeatureDefinitionModel({
            featureKind: input.featureKind,
            version: input.version ?? 1,
            commandParams: input.commandParams,
            normalizedDefinition: normalizeExtrudeFeatureDefinition(input.commandParams),
        });
    }

    /**
     * @param snapshot 拉伸特征定义快照。
     */
    public constructor(snapshot: ExtrudeFeatureDefinitionSnapshot) {
        super(snapshot);
    }

    /**
     * 获取原始命令参数快照。
     *
     * @returns 原始命令参数快照。
     */
    public getCommandParams(): ExtrudeParams {
        return this.toSnapshot().commandParams;
    }

    /**
     * 获取源草图 ID。
     *
     * 设计说明：
     * 1. `Viewer`、`Prototype` 与设计树投影链都需要稳定读取拉伸特征来源草图；
     * 2. 当前拉伸定义的单一真相仍在 `commandParams.sketchId`；
     * 3. 通过定义对象提供统一读取入口，避免上层继续直接解构原始命令参数。
     *
     * @returns 草图 ID；不存在时返回 `null`。
     */
    public getSourceSketchId(): string | null {
        const sketchId = this.toSnapshot().commandParams.sketchId;
        if (typeof sketchId !== 'string') {
            return null;
        }
        const trimmedSketchId = sketchId.trim();
        return trimmedSketchId.length > 0 ? trimmedSketchId : null;
    }

    /**
     * 获取归一化后的拉伸定义。
     *
     * @returns 归一化后的拉伸定义快照。
     */
    public getNormalizedDefinition(): NormalizedExtrudeFeatureDefinition {
        return this.toSnapshot().normalizedDefinition;
    }

    /**
     * 获取当前拉伸操作类型。
     *
     * @returns 操作类型。
     */
    public getOperation(): ExtrudeOperation {
        return resolveExtrudeOperationFromKind(this.getFeatureKind());
    }

    /**
     * 生成面向 UI 的定义投影快照。
     *
     * 说明：
     * 1. 投影快照由 definition 负责输出，web-client 不再自己回读旧 command params 再解释；
     * 2. mergeResult 作为定义层的一部分统一从 cad-core 投影出去；
     * 3. 该方法仅输出 UI 会话恢复所需的稳定字段，不暴露原始命令协议细节。
     *
     * @returns 定义投影快照。
     */
    public toProjection(): ExtrudeFeatureDefinitionProjection {
        const snapshot = this.toSnapshot();
        return {
            featureKind: snapshot.featureKind,
            operation: resolveExtrudeOperationFromKind(snapshot.featureKind),
            direction1: {
                ...snapshot.normalizedDefinition.direction1,
            },
            direction2: {
                ...snapshot.normalizedDefinition.direction2,
            },
            thinFeature: {
                ...snapshot.normalizedDefinition.thinFeature,
            },
            mergeResult: typeof snapshot.commandParams.mergeResult === 'boolean'
                ? snapshot.commandParams.mergeResult
                : true,
            cutOptions: ExtrudeCutDefinitionSnapshotBuilder.clone(snapshot.commandParams.cutOptions),
        };
    }

    /**
     * 基于新的命令参数生成新的定义对象。
     *
     * @param commandParams 新的原始命令参数。
     * @returns 新的拉伸特征定义对象。
     */
    public withCommandParams(commandParams: ExtrudeParams): ExtrudeFeatureDefinitionModel {
        return ExtrudeFeatureDefinitionModel.fromParams({
            featureKind: this.getFeatureKind(),
            version: this.getVersion(),
            commandParams,
        });
    }
}
